import { useState } from 'preact/hooks';
import type { OnsiteProduct, OnsitePrice, TrialDiscount, ChargedTotals } from '@/types';
import { CheckoutShell } from '@/components/CheckoutShell';
import { cadenceSuffix, OrderSummary } from '@/components/OrderSummary';
import { PromoCodeInput, type PromoApplyResult } from '@/components/PromoCodeInput';
import { couponChipLabel } from '@/components/promoDescriptor';
import { t, useT } from '@/i18n/t';
import { formatMinorUnits } from '@/money';

interface Props {
  product?: OnsiteProduct;
  price?: OnsitePrice;
  // The promo the visitor applied on the trial form, carried to the convert.
  // Shown as an applied chip (removable) plus the sidebar discount note, so the
  // acknowledgment never states the full price for a discounted charge.
  promoCode?: string | null;
  discount?: TrialDiscount | null;
  // Stripe's invoice-preview totals for the convert's immediate first charge
  // (from the validate response). When present the sidebar shows the real
  // Subtotal/Discount/Total breakdown instead of the catalog price plus a
  // note.
  firstCharge?: ChargedTotals | null;
  // Whether the domain has active promo codes (from the trial start). Drives
  // the entry toggle when no code is applied; an applied code always renders
  // (removable) regardless.
  promotionCodesEnabled?: boolean;
  // Applies a visitor-entered code to the pending convert. The parent
  // re-validates against the selected product/price (the convert re-validates
  // server-side regardless) and, on success, re-pins the code + discount.
  onApplyPromo: (code: string) => Promise<PromoApplyResult>;
  // Drops the carried code from the pending convert. Purely local: the code is
  // only attached at convert, so there is nothing server-side to undo.
  onRemovePromo: () => Promise<PromoApplyResult>;
  // Set once a convert has created the subscription: the code is counted at
  // create and the retry must replay identical parameters.
  promoLocked?: boolean;
  // Server-masked email the trial ran under; display-only. Converting charges
  // the saved card, so the buyer sees which identity the subscription lands
  // on. No "Not you?" switch — Back already exits the acknowledgment.
  maskedEmail?: string;
  // Subscribe now on the card already saved by the SetupIntent (no re-entry).
  onSubscribe: () => void;
  // Set only after a failed payment: reopens the card form for a new card.
  onAddPaymentMethod?: () => void;
  onBack: () => void;
  onDismiss?: () => void;
  loading: boolean;
  error: string | null;
}

function formatAmount(amountCents: number, currency: string): string {
  return formatMinorUnits(amountCents, currency);
}

function formatRecurring(price: OnsitePrice): string {
  return formatRecurringAmount(price.amountCents, price.currency, price);
}

function formatRecurringAmount(amountCents: number, currency: string, price: OnsitePrice): string {
  return `${formatAmount(amountCents, currency)}${cadenceSuffix(price.billingInterval, price.billingIntervalCount)}`;
}

// consentCopy builds the acknowledgment checkbox and Subscribe button text.
// When the validate preview carried the convert's first-charge totals, both
// state the actual charge rather than the catalog price: forever bills the
// discounted amount every cycle, once bills it today and the catalog price
// after, repeating bills it for the exact month count first. A repeating
// discount without a month count, or a missing preview, keeps the
// catalog-price copy (the sidebar note still states the discount).
function consentCopy(price: OnsitePrice | undefined, priceLabel: string, promoCode: string | null | undefined, discount: TrialDiscount | null | undefined, firstCharge: ChargedTotals | null | undefined): { ack: string; subscribe: string } {
  const catalog = {
    ack: priceLabel ? t('trialIneligible.ackWithPrice', { price: priceLabel }) : t('trialIneligible.ack'),
    subscribe: priceLabel ? t('trialIneligible.subscribeFor', { price: priceLabel }) : t('trialIneligible.subscribeNow'),
  };
  if (!priceLabel || !promoCode || !discount || !firstCharge || !price) {
    return catalog;
  }
  const todayRecurring = formatRecurringAmount(firstCharge.amountCents, firstCharge.currency, price);
  if (discount.duration === 'forever') {
    return {
      ack: t('trialIneligible.ackWithPrice', { price: todayRecurring }),
      subscribe: t('trialIneligible.subscribeFor', { price: todayRecurring }),
    };
  }
  if (discount.duration === 'repeating') {
    // The month-count copy only reads true on a monthly price: on longer
    // intervals a short month window covers just the first invoice, so those
    // fall through to the once-style copy, which states today's charge exactly.
    if (discount.durationInMonths && price.billingInterval === 'month') {
      return {
        ack: t('trialIneligible.ackDiscountRepeating', { today: todayRecurring, n: discount.durationInMonths, count: discount.durationInMonths, price: priceLabel }),
        subscribe: t('trialIneligible.subscribeFor', { price: todayRecurring }),
      };
    }
    // No month count → the window can't be stated; keep the catalog copy (the
    // sidebar note still states the discount).
    if (!discount.durationInMonths) {
      return catalog;
    }
  }
  const today = formatAmount(firstCharge.amountCents, firstCharge.currency);
  return {
    ack: t('trialIneligible.ackDiscountOnce', { today, price: priceLabel }),
    subscribe: t('trialIneligible.subscribeToday', { today }),
  };
}

// TrialIneligibleStep is the trial-gate acknowledgment: the visitor or the
// entered card already used a free trial for this product, so no trial is
// granted. Both gates deny at confirm time, when the card is already saved to
// the customer (the SetupIntent succeeded), so the visitor can start a paid
// subscription on it now without re-entering anything.
export function TrialIneligibleStep({ product, price, promoCode, discount, firstCharge, promotionCodesEnabled, onApplyPromo, onRemovePromo, promoLocked, maskedEmail, onSubscribe, onAddPaymentMethod, onBack, onDismiss, loading, error }: Props) {
  useT();
  // The visitor must explicitly acknowledge they are NOT getting a free trial
  // before a paid subscription can be created — converting a denied trial to a
  // charge without consent would be misleading.
  const [acknowledged, setAcknowledged] = useState(false);
  // PromoCodeInput's busy state is internal; track the parent's apply/remove
  // promise here so Subscribe can't fire mid-validation and convert without
  // the code the chip is about to show.
  const [promoBusy, setPromoBusy] = useState(false);
  const applyPromo = async (code: string): Promise<PromoApplyResult> => {
    setPromoBusy(true);
    try {
      return await onApplyPromo(code);
    } finally {
      setPromoBusy(false);
    }
  };
  const removePromo = async (): Promise<PromoApplyResult> => {
    setPromoBusy(true);
    try {
      return await onRemovePromo();
    } finally {
      setPromoBusy(false);
    }
  };
  const priceLabel = price ? formatRecurring(price) : '';
  const promoLabel = price && promoCode && discount ? couponChipLabel(price, discount) : undefined;
  const copy = consentCopy(price, priceLabel, promoCode, discount, firstCharge);
  // The promo entry sits in the sidebar under the order summary whose totals
  // it changes — the same placement as the payment steps.
  const showPromo = !!(promotionCodesEnabled || promoCode);
  const sidebar = (product && price) || showPromo
    ? (
      <>
        {product && price && <OrderSummary product={product} price={price} discount={discount} charged={firstCharge} />}
        {showPromo && (
          <PromoCodeInput
            onApply={applyPromo}
            onRemove={removePromo}
            appliedCode={promoCode ?? null}
            discountLabel={promoLabel}
            disabled={loading || promoLocked}
          />
        )}
      </>
    )
    : undefined;

  return (
    <CheckoutShell ariaLabel={t('trialIneligible.eyebrow')} currentStep={2} sidebar={sidebar} onDismiss={onDismiss}>
      <div class="sm-eyebrow">{t('trialIneligible.eyebrow')}</div>
      <h2 class="sm-title">{t('trialIneligible.title')}</h2>
      <p class="sm-subtitle">
        {priceLabel
          ? t('trialIneligible.subtitleWithPrice', { price: priceLabel })
          : t('trialIneligible.subtitle')}
      </p>

      {maskedEmail && (
        <p class="sm-identity-note">
          {t('paywall.purchasingAs')} <strong>{maskedEmail}</strong>
        </p>
      )}

      <label class="sm-ack">
        <input
          type="checkbox"
          checked={acknowledged}
          onInput={(e) => setAcknowledged((e.target as HTMLInputElement).checked)}
          disabled={loading}
        />
        <span>{copy.ack}</span>
      </label>

      {error && <p class="sm-error" role="alert">{error}</p>}
      {onAddPaymentMethod && (
        <button class="sm-secondary-link" type="button" onClick={onAddPaymentMethod} disabled={loading}>
          {t('trialIneligible.addPaymentMethod')}
        </button>
      )}

      <button
        type="button"
        class="sm-btn sm-btn-primary"
        onClick={onSubscribe}
        disabled={loading || !acknowledged || promoBusy}
      >
        {loading && <span class="sm-btn-spinner" aria-hidden="true" />}
        {loading ? t('trialIneligible.starting') : copy.subscribe}
      </button>

      <button
        class="sm-btn sm-btn-secondary"
        type="button"
        onClick={onBack}
        disabled={loading}
      >
        {t('common.back')}
      </button>
    </CheckoutShell>
  );
}
